const { attachSource } = require('./mediaSource');

class VideoLoader {
  constructor() {
    this.players = {}; // Initialize players map
    console.log('VideoLoader initialized');
  }

  /**
   * @param {jQuery|HTMLElement} container
   * @param {string} videoUrl
   * @param {boolean} autoplay
   * @param {Object} [options]
   * @param {boolean} [options.playOnReady=true] Start playback as soon as the player is ready (muted).
   * @param {string}  [options.poster]           Poster image for HTML5 videos (MP4/HLS).
   * @param {string}  [options.ratio]            Aspect ratio for Plyr, e.g. "9:16".
   * @param {boolean} [options.lazyLoad=false]   Only load the stream once playback starts.
   * @param {Object}  [options.player]           Player config from PHP: {controls: [], settings: [], behavior: []}.
   */
  load(container, videoUrl, autoplay = false, options = {}) {
    this.container = jQuery(container);
    this.videoUrl = videoUrl;
    this.autoplay = autoplay;
    this.playOnReady = options.playOnReady !== undefined ? options.playOnReady : true;
    this.poster = options.poster || null;
    this.ratio = options.ratio || null;
    this.lazyLoad = !!options.lazyLoad;
    this.playerConfig = options.player || null;

    // Prefer provider/id coming from PHP if available
    this.provider = this.container.data('video-provider') || null;
    this.videoId = this.container.data('video-id') || null;


    // Initialize the video player
    return this.initPlayer();
  }

  initPlayer() {
    this.container.empty();

    let videoElement;
    const uniqueId = 'plyr-' + Math.random().toString(36).substr(2, 9);

    if (this.provider === 'vimeo' && this.videoId) {
      videoElement = jQuery('<div>', {
        id: uniqueId,
        'data-plyr-provider': 'vimeo',
        'data-plyr-embed-id': this.videoId
      });

    } else if (this.provider === 'youtube' && this.videoId) {
      videoElement = jQuery('<div>', {
        id: uniqueId,
        'data-plyr-provider': 'youtube',
        'data-plyr-embed-id': this.videoId
      });

    } else {
      // Fall back to old auto-detection

      if (this.isVimeoUrl(this.videoUrl)) {
        videoElement = jQuery('<div>', {
          'id': uniqueId,
          'data-plyr-provider': 'vimeo',
          'data-plyr-embed-id': this.extractVimeoId(this.videoUrl)
        });
      } else if (this.isYoutubeUrl(this.videoUrl)) {
        videoElement = jQuery('<div>', {
          'id': uniqueId,
          'data-plyr-provider': 'youtube',
          'data-plyr-embed-id': this.extractYoutubeId(this.videoUrl)
        });
      } else if (this.isMp4Url(this.videoUrl)) {
        videoElement = jQuery('<video>', {
          'id': uniqueId,
          src: this.videoUrl,
          playsinline: true, // Important for iOS and inline playback
        });
      } else if (this.isHlsUrl(this.videoUrl)) {
        videoElement = jQuery('<video>', {
          'id': uniqueId,
          playsinline: true,
        });
      } else {
        console.error('Unsupported video URL:', this.videoUrl);
        return Promise.reject('Unsupported video URL');
      }

    }

    // HTML5 videos (MP4/HLS): poster image + don't fetch data before playback when lazy
    if (videoElement.is('video')) {
      if (this.poster) {
        videoElement.attr('poster', this.poster);
      }
      if (this.lazyLoad) {
        videoElement.attr('preload', 'none');
      }
    }

    this.container.append(videoElement);

    return new Promise((resolve, reject) => {
      try {
        // Quality Mappings of Bunny CDN - https://dash.bunny.net/stream/.../encoding
        const qualityMapping = { '240': 0, '360': 1, '480': 2, '720': 3, '1080': 4, /*'1440': 5, '2160': 6*/ };

        const plyrIconUrl = (window.$madSettings && window.$madSettings.plyrIconUrl) || null;

        // Controls / settings menu / behavior from the video settings (see mad_videos_shortcode())
        const config = this.playerConfig;
        const behavior = config ? (config.behavior || []) : ['loop', 'click_to_play', 'hide_controls', 'keyboard', 'invert_time'];
        const has = (key) => behavior.includes(key);
        const configOptions = config
          ? { controls: config.controls || ['play-large'], settings: config.settings || [] }
          : { settings: ['quality', 'speed'] }; // no config: Plyr's default controls

        const player = new Plyr(videoElement[0], {
          ...configOptions,
          clickToPlay: has('click_to_play'),
          hideControls: has('hide_controls'),
          keyboard: { focused: has('keyboard'), global: false },
          tooltips: { controls: has('tooltips'), seek: true },
          invertTime: has('invert_time'),
          iconUrl: plyrIconUrl,
          loadSprite: true,
          iconPrefix: 'plyr',
          muted: this.playOnReady, // browsers only allow muted autoplay
          autoplay: this.playOnReady,
          ratio: this.ratio,
          loop: { active: has('loop') },
          storage: { enabled: false },
          playsinline: true,
          quality: {
            default: '720',
            options: Object.keys(qualityMapping).reverse(),
            forced: true,
            onChange: (newQuality) => {
              const hlsInstance = videoElement.data('hls');
              if (hlsInstance) {
                hlsInstance.currentLevel = qualityMapping[newQuality];
              }
            }
          }
        });
        this.players[uniqueId] = player;

        player.on('ready', () => {
          // Replace Plyr's SVG icon with a dashicon to match the pre-play overlay button
          const overlaidBtn = this.container[0].querySelector('.plyr__control--overlaid');
          if (overlaidBtn) {
            const svg = overlaidBtn.querySelector('svg');
            if (svg) svg.remove();
            if (!overlaidBtn.querySelector('.dashicons')) {
              const icon = document.createElement('span');
              icon.className = 'dashicons dashicons-controls-play';
              overlaidBtn.insertBefore(icon, overlaidBtn.firstChild);
            }
          }

          if (this.isHlsUrl(this.videoUrl)) {
            this.initializeHlsPlayer(player, videoElement[0], resolve, reject);
          } else {
            if (this.playOnReady) {
              player.play().catch(e => console.warn(`Autoplay was prevented for ${this.videoUrl}:`, e));
            }
            resolve(player);
          }
        });

        player.on('error', (event) => {
          console.error('Plyr Error:', event.detail.plyr.source, event);
          reject(event);
        });

      } catch (error) {
        console.error('Error initializing Plyr:', error);
        reject(error);
      }
    });
  }

  initializeHlsPlayer(player, videoElement, resolve, reject) {
    let source;
    try {
      source = attachSource(videoElement, this.videoUrl, { lazy: this.lazyLoad, onError: reject });
    } catch (error) {
      console.error(error.message);
      reject('HLS not supported');
      return;
    }

    if (source.hls) {
      jQuery(videoElement).data('hls', source.hls); // used by the quality switch
    }
    if (this.lazyLoad) {
      player.once('play', () => source.startLoad());
    }

    resolve(player);
  }

  isVimeoUrl(url) {
    const vimeoRegex = /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
    return vimeoRegex.test(url);
  }

  isMp4Url(url) {
    const mp4Regex = /\.mp4$/i;
    return mp4Regex.test(url);
  }

  isHlsUrl(url) {
    const hlsRegex = /\.m3u8$/i;
    return hlsRegex.test(url);
  }

  extractVimeoId(url) {
    const vimeoRegex = /(?:https?:\/\/)?(?:www\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/;
    const match = url.match(vimeoRegex);
    return match ? match[1] : null;
  }

  isYoutubeUrl(url) {
    if (!url) return false;
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.?be)\/.+$/;
    return youtubeRegex.test(url);
  }

  extractYoutubeId(url) {
    if (!url) return null;
    const youtubeRegex =
      /(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^\"&?\/\s]{11})/;
    const match = url.match(youtubeRegex);
    return match ? match[1] : null;
  }
}

// Expose the VideoLoader class globally
window.VideoLoader = VideoLoader;

module.exports = VideoLoader;
